import React, { useMemo, useState } from "react";
import { CompanyData } from "@/pages/companies/types";
import DetailFields, {
  DetailFieldsContext,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  mapSectionsFieldsConfig,
  useEntityDetailFieldsLayout
} from "@/hooks/useEntityFieldsConfig";
import { useTranslation } from "react-i18next";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import { useSubIndustriesByIndustryId } from "@/entities/settings/hooks";
import { get } from "lodash";

interface Props {
  company?: CompanyData;
  onUpdateField: (field: string, newValue: string | number | string[]) => void;
  className?: string;
  containerClassName?: string;
  isDrawerOpen?: boolean;
  isQuickView?: boolean;
}

const CompanyDetailFields: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);

  const { data: entityFieldsResponse } = useEntityFields(EntityType.COMPANY);
  const updateEntityField = useUpdateEntityField(EntityType.COMPANY);

  const currentIndustryId = props.company?.industryId ?? null;
  const currentSubIndustryId = props.company?.subIndustryId ?? null;
  const { data: subIndustriesData } =
    useSubIndustriesByIndustryId(currentIndustryId);

  const subIndustryOptions = useMemo(() => {
    if (!subIndustriesData) return [];
    return (
      subIndustriesData
        // Exclude hidden values, but keep the one already set so it still renders.
        .filter(
          (subIndustry) =>
            !subIndustry.isHidden ||
            String(subIndustry.id) === String(currentSubIndustryId)
        )
        .map((subIndustry) => ({
          id: subIndustry.id,
          displayName: subIndustry.name
        }))
    );
  }, [subIndustriesData, currentSubIndustryId]);

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "companies",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      // Auto-select the newly created option
      if (props.onUpdateField) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          const currentValue = get(
            props.company,
            currentFieldForNewOption.fieldKey
          );
          const currentValues = Array.isArray(currentValue) ? currentValue : [];
          const newValues = [...currentValues, optionName];
          props.onUpdateField(currentFieldForNewOption.fieldKey, newValues);
        } else {
          props.onUpdateField(currentFieldForNewOption.fieldKey, optionName);
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const context: DetailFieldsContext = {
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption,
    onUpdateField: props.onUpdateField
  };

  const { fieldsConfig: baseFieldsConfig, sections: baseSections } =
    useEntityDetailFieldsLayout(EntityType.COMPANY, props.company, context, {
      groupBySection: !props.isQuickView
    });

  const { fieldsConfig, sections } = useMemo(() => {
    const withIndustryControls = (config: FieldsConfig): FieldsConfig => {
      const modifiedConfig = { ...config };

      if (
        modifiedConfig.industryId &&
        modifiedConfig.industryId.type === "select"
      ) {
        const originalOnChange = modifiedConfig.industryId.onChange;
        modifiedConfig.industryId = {
          ...modifiedConfig.industryId,
          showSearch: true,
          onChange: (value: string | number) => {
            props.onUpdateField("subIndustryId", "");
            originalOnChange?.(value);
          }
        };
      }

      if (
        modifiedConfig.subIndustryId &&
        modifiedConfig.subIndustryId.type === "select"
      ) {
        const isDisabled = currentIndustryId === null;
        modifiedConfig.subIndustryId = {
          ...modifiedConfig.subIndustryId,
          options: subIndustryOptions,
          disabled: isDisabled,
          showSearch: true
        };
      }

      return modifiedConfig;
    };

    return {
      fieldsConfig: withIndustryControls(baseFieldsConfig),
      sections: mapSectionsFieldsConfig(baseSections, withIndustryControls)
    };
  }, [
    baseFieldsConfig,
    baseSections,
    currentIndustryId,
    subIndustryOptions,
    props.onUpdateField
  ]);

  return (
    <>
      <DetailFields
        entity={props.company}
        fieldsConfig={fieldsConfig}
        sections={sections}
        onUpdateField={props.onUpdateField}
        className={props.className}
        containerClassName={props.containerClassName}
        tPrefix="companies.companyView"
        isDrawerOpen={props.isDrawerOpen}
        isQuickView={props.isQuickView}
        context={context}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => {
          setShowCreateOptionModal(false);
          setNewOptionName("");
          setCurrentFieldForNewOption(null);
        }}
        onCreateOption={context.handleCreateNewOption || (async () => {})}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
    </>
  );
};

export default CompanyDetailFields;
